import {
    MediaPack,
    WorkflowStepDataContentPack,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import { UseQueryResult } from '@rhg/libs/react-query';
import { usePacks } from '../use-packs';
import { RelatedIdWorkflowMap } from './types';
import { useProgramAndWorkflowIds } from './useProgramAndWorkflowIds';
import { useWorkflowQueries } from './useWorkflowQueries';

interface UseFetchPacksProps {
    circleId: number;
    showAll?: boolean;
}
interface UseFetchPacksResponse {
    isFetched: boolean;
    isPending: boolean;
    packs?: MediaPack[];
    contentPackWorkflowsNotComingSoon?: RelatedIdWorkflowMap;
}

function getContentPackWorkflowsNotComingSoon(
    workflowQueries?: UseQueryResult[]
): RelatedIdWorkflowMap | undefined {
    if (!workflowQueries) return undefined;
    return workflowQueries.reduce<RelatedIdWorkflowMap>(
        (workflowMap, query) => {
            const firstWorkflow =
                query.data && Array.isArray(query.data)
                    ? (query.data[0] as WorkflowWorkflowInfo)
                    : undefined;
            const firstStep: WorkflowStepDataContentPack | undefined =
                firstWorkflow?.steps.find(
                    (step) => step.stepDefn.type === 'content_pack'
                )?.stepDefn.data as WorkflowStepDataContentPack;

            return !firstWorkflow || !firstStep
                ? workflowMap
                : {
                      ...workflowMap,
                      [firstStep.relatedId]: firstWorkflow,
                  };
        },
        {}
    );
}

function getPackClassesFromWorkflows(
    contentPackWorkflowsNotComingSoon?: RelatedIdWorkflowMap
) {
    if (!contentPackWorkflowsNotComingSoon) return undefined;
    return Object.entries(contentPackWorkflowsNotComingSoon).map(
        ([packClass]) => packClass
    );
}

export function usePacksForWorkflows({
    showAll,
    circleId,
}: UseFetchPacksProps): UseFetchPacksResponse {
    const {
        workflowDefnIds,
        isFetched: hasFetchedCircleRecommendations,
        isPending: isPendingCircleRecommendations,
    } = useProgramAndWorkflowIds({
        circleId,
        showAll,
    });

    const {
        workflowQueries,
        isFetched: hasFetchedAllWorkflows,
        isPending: isPendingWorkflowQueries,
    } = useWorkflowQueries({
        hasFetchedCircleRecommendations,
        workflowDefnIds,
        circleId,
    });

    const contentPackWorkflowsNotComingSoon =
        getContentPackWorkflowsNotComingSoon(workflowQueries);
    const packClasses = getPackClassesFromWorkflows(
        contentPackWorkflowsNotComingSoon
    );
    const {
        isFetched: hasFetchedPacks,
        packs,
        isPending: isPendingPacks,
    } = usePacks(packClasses, circleId, hasFetchedAllWorkflows);

    const isPending =
        isPendingCircleRecommendations ||
        isPendingPacks ||
        isPendingWorkflowQueries;
    return {
        packs,
        isFetched:
            hasFetchedAllWorkflows &&
            (hasFetchedPacks || !workflowQueries?.length),
        contentPackWorkflowsNotComingSoon,
        isPending,
    };
}
